import { isValidElement, useEffect, useRef, useState, type ReactNode } from 'react';

/*
 * Motion for the CK apps. One idea, used everywhere: a warm gold light that answers the pointer.
 * Everything here switches off for people who ask their device for reduced motion.
 */
const still = () => typeof window === 'undefined' || !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Splits "$12,345.67 USD" into a prefix, a number and a suffix so only the number counts up. */
function split(s: string) {
  const m = /^([^0-9-]*)(-?[0-9][0-9,]*(?:\.[0-9]+)?)(.*)$/.exec(s);
  if (!m) return null;
  const raw = m[2]!;
  const n = Number(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const decimals = raw.includes('.') ? raw.split('.')[1]!.length : 0;
  return { pre: m[1]!, n, decimals, grouped: raw.includes(','), post: m[3]! };
}
const fmt = (v: number, decimals: number, grouped: boolean) =>
  grouped ? v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : v.toFixed(decimals);

/** A figure that counts up from zero the first time it appears (and from its last value when it changes). */
export function CountUp({ value, duration = 900 }: { value: ReactNode; duration?: number }) {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : null;
  const parts = text ? split(text) : null;
  const [shown, setShown] = useState<number | null>(parts && !still() ? 0 : null);
  const from = useRef(0);
  useEffect(() => {
    if (!parts || still()) { setShown(null); return; }
    let raf = 0; const start = performance.now(); const a = from.current, b = parts.n;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / duration), e = 1 - Math.pow(1 - k, 3);
      setShown(a + (b - a) * e);
      if (k < 1) raf = requestAnimationFrame(step); else { from.current = b; setShown(null); }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  if (!parts || shown === null || isValidElement(value)) return <>{value}</>;
  return <span className="count-up" aria-label={text ?? undefined}><span aria-hidden="true">{parts.pre}{fmt(shown, parts.decimals, parts.grouped)}{parts.post}</span></span>;
}

const LIT = '.panel, .stat, .stats > *, .acc, .card, .tile, .kpi, .desk-item, .lb li, .cu-card, .plan-card, .ptab';

/** The pointer casts a soft gold light on whichever card it is over (one listener for the whole app). */
export function initSpotlight() {
  if (typeof window === 'undefined' || still() || !window.matchMedia?.('(hover: hover)').matches) return;
  let last: HTMLElement | null = null;
  window.addEventListener('pointermove', (e) => {
    const el = (e.target as Element | null)?.closest?.(LIT) as HTMLElement | null;
    if (last && last !== el) last.classList.remove('lit');
    last = el;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
    el.classList.add('lit');
  }, { passive: true });
  document.addEventListener('pointerleave', () => { last?.classList.remove('lit'); last = null; });
}

/** A short burst of gold sparks from main buttons when they are pressed. */
export function initClickSpark() {
  if (typeof window === 'undefined' || still()) return;
  document.addEventListener('pointerdown', (e) => {
    const btn = (e.target as Element | null)?.closest?.('.btn:not(.ghost):not(.sm):not(:disabled), .spark-on') as HTMLElement | null;
    if (!btn) return;
    const burst = document.createElement('span');
    burst.className = 'spark-burst';
    burst.style.left = `${e.clientX}px`; burst.style.top = `${e.clientY}px`;
    for (let i = 0; i < 8; i++) { const s = document.createElement('i'); s.style.setProperty('--a', `${i * 45}deg`); burst.appendChild(s); }
    document.body.appendChild(burst);
    setTimeout(() => burst.remove(), 600);
  }, { passive: true });
}

/** Wraps a page so it rises in once when it opens. Give it key={pageId}. */
export function PageIn({ children }: { children: ReactNode }) {
  return <div className="page-in">{children}</div>;
}
